/**
 * `DesignNode` → CSS.
 *
 * One place that knows how a node's geometry and style become inline styles, so
 * the canvas, the thumbnail and any future published renderer paint a node
 * identically. Returns plain `Record<string, string>` objects for `[ngStyle]`.
 */

import type {
  AutoLayout,
  DesignNode,
  Edges,
  Fill,
  Stroke,
  TextContent,
} from '../design-builder.types';

/** Geometry + appearance for a node's own box. */
export function nodeStyles(node: DesignNode): Record<string, string> {
  const style: Record<string, string> = {
    position: 'absolute',
    left: `${node.x}px`,
    top: `${node.y}px`,
    width: `${node.width}px`,
    height: `${node.height}px`,
    opacity: `${node.opacity}`,
  };

  if (node.rotation) {
    style['transform'] = `rotate(${node.rotation}deg)`;
    style['transform-origin'] = 'center center';
  }
  if (!node.visible) style['display'] = 'none';

  Object.assign(style, appearanceStyles(node));
  if (node.type === 'FRAME' && node.layout && node.layout.mode !== 'none') {
    Object.assign(style, autoLayoutStyles(node.layout));
  }
  if (node.type === 'TEXT' && node.text) {
    Object.assign(style, textStyles(node.text));
  }
  return style;
}

/** A stroke's colour at its opacity % (Figma's stroke "%"); 100 / absent = the colour as is. */
export function strokeColor(stroke: Stroke): string {
  const opacity = Number(stroke.opacity);
  if (!Number.isFinite(opacity) || opacity >= 100) return stroke.color;
  return `color-mix(in srgb, ${stroke.color} ${Math.max(0, Math.round(opacity))}%, transparent)`;
}

/** Fill, stroke, radius, shadow, blur, padding, overflow — no geometry. */
export function appearanceStyles(node: DesignNode): Record<string, string> {
  const style: Record<string, string> = {};
  const { fill, stroke, radius, shadow, blur, padding, overflow } = node.style ?? {};

  // A LINE is drawn as its stroke, not as a filled box. Which axis the stroke
  // thickens is taken from the shape itself — drag it taller than it is wide and
  // it becomes vertical — because pinning the thickness to `height` made a
  // vertical line impossible to draw and forced authors to rotate a horizontal
  // one instead. Any angle in between is still the node's own rotation.
  if (node.type === 'LINE') {
    const thickness = Math.max(1, stroke?.width ?? 1);
    style['background'] = stroke ? strokeColor(stroke) : '#98a2b3';
    style[(node.height ?? 0) > (node.width ?? 0) ? 'width' : 'height'] = `${thickness}px`;
    return style;
  }

  const background = fillToCss(fill);
  if (background) style['background'] = background;

  if (stroke && stroke.width > 0) {
    style['border'] = `${stroke.width}px ${stroke.style} ${strokeColor(stroke)}`;
    // Keep the drawn box the size the inspector reports.
    style['box-sizing'] = 'border-box';
  }

  const radiusCss = radiusToCss(radius, node.type === 'ELLIPSE');
  if (radiusCss) style['border-radius'] = radiusCss;

  if (shadow) {
    style['box-shadow'] = `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.spread}px ${shadow.color}`;
  }
  if (blur) style['filter'] = `blur(${blur}px)`;
  if (padding) style['padding'] = edgesToCss(padding);
  if (overflow) style['overflow'] = overflow;

  return style;
}

export function fillToCss(fill: Fill | undefined): string {
  if (!fill || fill.kind === 'none') return '';
  if (fill.kind === 'solid') return fill.color ?? 'transparent';
  const stops = (fill.stops ?? [])
    .map((stop) => `${stop.color} ${Math.round(stop.offset * 100)}%`)
    .join(', ');
  return stops ? `linear-gradient(${fill.angle ?? 90}deg, ${stops})` : '';
}

function radiusToCss(radius: DesignNode['style']['radius'], isEllipse: boolean): string {
  if (isEllipse) return '50%';
  if (radius === undefined || radius === null) return '';
  if (Array.isArray(radius)) return radius.map((value) => `${value}px`).join(' ');
  return radius > 0 ? `${radius}px` : '';
}

export function edgesToCss(edges: Edges): string {
  return edges.map((value) => `${value}px`).join(' ');
}

export function autoLayoutStyles(layout: AutoLayout): Record<string, string> {
  const justifyMap: Record<string, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    between: 'space-between',
  };
  const alignMap: Record<string, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    stretch: 'stretch',
  };
  return {
    display: 'flex',
    'flex-direction': layout.mode === 'row' ? 'row' : 'column',
    gap: `${layout.gap}px`,
    padding: edgesToCss(layout.padding),
    'align-items': alignMap[layout.align] ?? 'flex-start',
    'justify-content': justifyMap[layout.justify] ?? 'flex-start',
    'flex-wrap': layout.wrap ? 'wrap' : 'nowrap',
  };
}

export function textStyles(text: TextContent): Record<string, string> {
  const style: Record<string, string> = {
    'font-family': text.fontFamily,
    'font-size': `${text.fontSize}px`,
    'font-weight': `${text.fontWeight}`,
    color: text.color,
    'text-align': text.align,
    'line-height': `${text.lineHeight}`,
    'letter-spacing': `${text.letterSpacing}px`,
    'white-space': 'pre-wrap',
    'word-break': 'break-word',
  };
  if (text.fontStyle && text.fontStyle !== 'normal') style['font-style'] = text.fontStyle;
  if (text.textTransform && text.textTransform !== 'none') style['text-transform'] = text.textTransform;
  if (text.textDecoration && text.textDecoration !== 'none') style['text-decoration'] = text.textDecoration;

  // Vertical alignment needs the text node to be a flex column.
  if (text.verticalAlign && text.verticalAlign !== 'top') {
    style['display'] = 'flex';
    style['flex-direction'] = 'column';
    style['justify-content'] = text.verticalAlign === 'middle' ? 'center' : 'flex-end';
  }
  return style;
}

/**
 * A node inside an auto-layout frame is positioned by flex, not by x/y — its
 * absolute coordinates would fight the layout. Children of such frames get this
 * override instead.
 */
export function flowChildStyles(node: DesignNode): Record<string, string> {
  const style = nodeStyles(node);
  delete style['position'];
  delete style['left'];
  delete style['top'];
  style['flex'] = '0 0 auto';
  return style;
}

/**
 * The infinite workspace grid.
 *
 * Drawn on the VIEWPORT, not the artboard, so it covers the whole empty space
 * the way a design tool's canvas does — a grid that stopped at the page edge
 * left a hard rectangle floating in blank grey.
 *
 * It is a repeating background offset by the pan and scaled by the zoom, so it
 * stays locked to document coordinates as you move around without needing a
 * single DOM node per line.
 */
export function gridBackground(
  size: number,
  zoom: number,
  panX = 0,
  panY = 0,
): Record<string, string> {
  const step = size * zoom;
  // Below ~4 screen px the grid turns into noise, so it simply stops drawing.
  if (step < 4) return {};
  return {
    'background-image':
      'linear-gradient(to right, rgba(16,24,40,0.07) 1px, transparent 1px),' +
      'linear-gradient(to bottom, rgba(16,24,40,0.07) 1px, transparent 1px)',
    'background-size': `${step}px ${step}px`,
    // Keeps the lines aligned to the document origin while panning.
    'background-position': `${panX}px ${panY}px`,
  };
}

/** Every Nth layout-grid line is drawn stronger, so columns are easy to count. */
const LAYOUT_GRID_MAJOR_EVERY = 8;

/**
 * The layout grid overlay: drawn over the frames, so it must read against both
 * white pages and charts. Minor lines drop out when they would be noise; the
 * major lines stay as long as they are a usable distance apart.
 */
export function layoutGridOverlay(
  size: number,
  zoom: number,
  panX = 0,
  panY = 0,
): Record<string, string> {
  const step = size * zoom;
  const majorStep = step * LAYOUT_GRID_MAJOR_EVERY;
  if (majorStep < 16) return {};

  const major = 'rgba(41,112,255,0.35)';
  const minor = 'rgba(41,112,255,0.14)';
  const layers = [
    `linear-gradient(to right, ${major} 1px, transparent 1px)`,
    `linear-gradient(to bottom, ${major} 1px, transparent 1px)`,
  ];
  const sizes = [`${majorStep}px ${majorStep}px`, `${majorStep}px ${majorStep}px`];
  if (step >= 6) {
    layers.push(
      `linear-gradient(to right, ${minor} 1px, transparent 1px)`,
      `linear-gradient(to bottom, ${minor} 1px, transparent 1px)`,
    );
    sizes.push(`${step}px ${step}px`, `${step}px ${step}px`);
  }
  const position = `${panX}px ${panY}px`;
  return {
    'background-image': layers.join(','),
    'background-size': sizes.join(','),
    'background-position': layers.map(() => position).join(','),
  };
}
